import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Plus, 
  CreditCard, 
  AlertTriangle, 
  Check, 
  Clock, 
  Search, 
  FileText, 
  Download, 
  MoreVertical,
  FileSpreadsheet,
  Calendar,
  Filter,
  Upload,
  Link,
  X,
  RefreshCw,
  Eye,
  Loader2,
  Trash2
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { format, subMonths, startOfMonth, endOfMonth, startOfYear, endOfYear, subYears } from "date-fns";
import { de } from "date-fns/locale";

const BRAND_COLOR = "#c11718";

export default function PaymentsPage() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvImportData, setCsvImportData] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvImportMonth, setCsvImportMonth] = useState(format(new Date(), "yyyy-MM")); // Default: current month
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [formData, setFormData] = useState({ booking_id: "", amount: "", due_date: "", description: "" });
  const [recordData, setRecordData] = useState({ amount: "", payment_method: "bank_transfer", reference: "", notes: "" });
  const [reportFilter, setReportFilter] = useState({
    preset: "this_month",
    start_date: format(startOfMonth(new Date()), "yyyy-MM-dd"),
    end_date: format(endOfMonth(new Date()), "yyyy-MM-dd"),
    report_type: "all"
  });
  // PDF Preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewFilename, setPreviewFilename] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (retryCount = 0) => {
    setLoadError(false);
    try {
      const [paymentsRes, bookingsRes] = await Promise.all([api.get("/payments"), api.get("/bookings")]);
      setPayments(paymentsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      
      // Network hatası ve henüz 3 kez denemediyse tekrar dene
      if (!error.response && retryCount < 3) {
        console.log(`Retrying... attempt ${retryCount + 1}`);
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return fetchData(retryCount + 1);
      }
      
      if (error.response?.status === 401) {
        toast.error("Sitzung abgelaufen. Bitte melden Sie sich erneut an.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        setLoadError(true);
        toast.error("Daten konnten nicht geladen werden. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { ...formData, amount: parseFloat(formData.amount), due_date: new Date(formData.due_date).toISOString() });
      toast.success("Zahlung erstellt");
      setDialogOpen(false);
      setFormData({ booking_id: "", amount: "", due_date: "", description: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedPayment) return;
    try {
      await api.post(`/payments/${selectedPayment.id}/record`, { ...recordData, amount: parseFloat(recordData.amount) });
      toast.success("Zahlung verbucht");
      setRecordDialogOpen(false);
      setSelectedPayment(null);
      setRecordData({ amount: "", payment_method: "bank_transfer", reference: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Verbuchen");
    }
  };

  const openRecordDialog = (payment) => {
    setSelectedPayment(payment);
    setRecordData({ amount: (payment.amount - payment.paid_amount).toFixed(2), payment_method: "bank_transfer", reference: "", notes: "" });
    setRecordDialogOpen(true);
  };

  // PDF Preview functions
  const previewInvoice = async (payment) => {
    try {
      // Guest ID'yi bul
      const booking = bookings.find(b => b.id === payment.booking_id);
      if (!booking) {
        toast.error("Buchung nicht gefunden - möglicherweise wurde der Bewohner gelöscht");
        return;
      }
      
      // Önce dialog'u loading state ile aç
      setPreviewPdf(null);
      setPreviewLoading(true);
      setPreviewTitle("Rechnung");
      setPreviewFilename(`Rechnung_${payment.id.slice(0, 8)}.pdf`);
      setPreviewDialogOpen(true);
      
      const response = await api.post(`/guests/${booking.guest_id}/document-preview`, {
        document_type: "invoice",
        month: new Date(payment.due_date).getMonth() + 1,
        year: new Date(payment.due_date).getFullYear()
      });
      
      setPreviewPdf(response.data.pdf_base64);
      setPreviewLoading(false);
    } catch (error) {
      setPreviewLoading(false);
      if (error.response?.status === 404) {
        toast.error("Bewohner nicht gefunden - möglicherweise wurde der Bewohner gelöscht");
      } else {
        toast.error("Fehler beim Laden der Vorschau");
      }
    }
  };

  const previewMahnung = async (payment, level) => {
    const levelNames = { 1: "Zahlungserinnerung", 2: "2. Mahnung", 3: "Letzte Mahnung" };
    try {
      const booking = bookings.find(b => b.id === payment.booking_id);
      if (!booking) {
        toast.error("Buchung nicht gefunden - möglicherweise wurde der Bewohner gelöscht");
        return;
      }
      
      // Önce dialog'u loading state ile aç
      setPreviewPdf(null);
      setPreviewLoading(true);
      setPreviewTitle(levelNames[level]);
      setPreviewFilename(`${levelNames[level].replace(/\s/g, "_")}_${payment.id.slice(0, 8)}.pdf`);
      setPreviewDialogOpen(true);
      
      const docTypes = { 1: "reminder1", 2: "reminder2", 3: "reminder3" };
      const response = await api.post(`/guests/${booking.guest_id}/document-preview`, {
        document_type: docTypes[level],
        month: new Date(payment.due_date).getMonth() + 1,
        year: new Date(payment.due_date).getFullYear()
      });
      
      setPreviewPdf(response.data.pdf_base64);
      setPreviewLoading(false);
    } catch (error) {
      setPreviewLoading(false);
      if (error.response?.status === 404) {
        toast.error("Bewohner nicht gefunden - möglicherweise wurde der Bewohner gelöscht");
      } else {
        toast.error("Fehler beim Laden der Vorschau");
      }
    }
  };

  const downloadCurrentPreview = () => {
    if (!previewPdf) return;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${previewPdf}`;
    link.download = previewFilename;
    link.click();
    toast.success("PDF heruntergeladen");
  };

  // Legacy download functions (kept for backwards compatibility)
  const downloadInvoice = async (paymentId) => {
    const payment = payments.find(p => p.id === paymentId);
    if (payment) previewInvoice(payment);
  };

  const downloadMahnung = async (paymentId, level) => {
    const payment = payments.find(p => p.id === paymentId);
    if (payment) previewMahnung(payment, level);
  };

  const handlePresetChange = (preset) => {
    const now = new Date();
    let start, end;
    
    switch (preset) {
      case "this_month":
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case "last_month":
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case "last_3_months":
        start = startOfMonth(subMonths(now, 2));
        end = endOfMonth(now);
        break;
      case "last_6_months":
        start = startOfMonth(subMonths(now, 5));
        end = endOfMonth(now);
        break;
      case "this_year":
        start = startOfYear(now);
        end = endOfYear(now);
        break;
      case "last_year":
        start = startOfYear(subYears(now, 1));
        end = endOfYear(subYears(now, 1));
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }
    
    setReportFilter({
      ...reportFilter,
      preset,
      start_date: format(start, "yyyy-MM-dd"),
      end_date: format(end, "yyyy-MM-dd")
    });
  };

  const downloadReportPdf = async () => {
    try {
      const response = await api.post("/reports/payments/pdf", {
        start_date: reportFilter.start_date,
        end_date: reportFilter.end_date,
        report_type: reportFilter.report_type
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zahlungsbericht_${reportFilter.start_date}_${reportFilter.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF-Bericht heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Erstellen des Berichts");
    }
  };

  const previewReportPdf = async () => {
    setPreviewLoading(true);
    try {
      const response = await api.post("/reports/payments/pdf-preview", {
        start_date: reportFilter.start_date,
        end_date: reportFilter.end_date,
        report_type: reportFilter.report_type
      });
      
      setPreviewPdf(response.data.pdf_data.replace('data:application/pdf;base64,', ''));
      setPreviewTitle("Z-Bericht Vorschau");
      setPreviewFilename(response.data.filename);
      setPreviewDialogOpen(true);
      setReportDialogOpen(false);
    } catch (error) {
      toast.error("Fehler beim Erstellen der Vorschau");
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadReportExcel = async () => {
    try {
      const response = await api.post("/reports/payments/excel", {
        start_date: reportFilter.start_date,
        end_date: reportFilter.end_date,
        report_type: reportFilter.report_type
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Zahlungsbericht_${reportFilter.start_date}_${reportFilter.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel-Bericht heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Erstellen des Berichts");
    }
  };

  // CSV Bank Import Functions
  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Bitte eine CSV-Datei auswählen");
      return;
    }
    
    setCsvUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const response = await api.post("/bank/import-csv", {
          csv_data: base64,
          csv_format: "sparkasse"
        });
        
        setCsvImportData(response.data);
        toast.success(`${response.data.total_transactions} Transaktionen importiert, ${response.data.matched_transactions} automatisch zugeordnet`);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("CSV upload error:", error);
      toast.error(error.response?.data?.detail || "Fehler beim CSV-Import");
    } finally {
      setCsvUploading(false);
    }
  };

  const handleManualMatch = async (transactionId, paymentId) => {
    if (!csvImportData) return;
    
    try {
      const tx = csvImportData.transactions.find(t => t.id === transactionId);
      await api.post(`/bank/imports/${csvImportData.import_id}/match`, {
        transaction_id: transactionId,
        payment_id: paymentId,
        amount: tx?.amount || 0
      });
      
      // Refresh import data
      const response = await api.get(`/bank/imports/${csvImportData.import_id}`);
      setCsvImportData({
        ...csvImportData,
        transactions: response.data.transactions,
        matched_transactions: response.data.matched_count
      });
      
      toast.success("Transaktion erfolgreich zugeordnet");
    } catch (error) {
      toast.error("Fehler bei der Zuordnung");
    }
  };

  const handleIgnoreTransaction = async (transactionId) => {
    if (!csvImportData) return;
    
    try {
      await api.post(`/bank/imports/${csvImportData.import_id}/ignore/${transactionId}`);
      
      // Update local state
      setCsvImportData({
        ...csvImportData,
        transactions: csvImportData.transactions.map(t => 
          t.id === transactionId ? { ...t, status: 'ignored' } : t
        )
      });
      
      toast.success("Transaktion ignoriert");
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleProcessImport = async () => {
    if (!csvImportData) return;
    
    setCsvProcessing(true);
    
    try {
      const response = await api.post(`/bank/imports/${csvImportData.import_id}/process`);
      toast.success(response.data.message);
      setCsvDialogOpen(false);
      setCsvImportData(null);
      fetchData(); // Refresh payments
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Verarbeiten");
    } finally {
      setCsvProcessing(false);
    }
  };

  // Admin only: Delete payment
  const handleDeletePayment = async (paymentId, guestName) => {
    if (user?.role !== "admin") {
      toast.error("Nur Administratoren können Zahlungen löschen");
      return;
    }
    
    if (!window.confirm(`Sind Sie sicher, dass Sie die Zahlung für "${guestName}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    
    try {
      await api.delete(`/payments/${paymentId}`);
      toast.success("Zahlung erfolgreich gelöscht");
      fetchData(); // Refresh payments
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid": return <Badge className="status-paid text-xs">Bezahlt</Badge>;
      case "pending": return <Badge className="status-pending text-xs">Offen</Badge>;
      case "overdue": return <Badge className="status-overdue text-xs">Überfällig</Badge>;
      case "partial": return <Badge className="status-pending text-xs">Teilweise</Badge>;
      default: return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const filteredPayments = payments.filter((p) => {
    const match = p.guest_name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.room_number?.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === "all") return match;
    return match && p.status === activeTab;
  });

  const stats = {
    pending: payments.filter((p) => p.status === "pending").length,
    overdue: payments.filter((p) => p.status === "overdue").length,
    paid: payments.filter((p) => p.status === "paid").length,
    totalPending: payments.filter((p) => p.status === "pending" || p.status === "overdue").reduce((acc, p) => acc + (p.amount - p.paid_amount), 0),
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="payments-page">
      {/* Error State with Retry Button */}
      {loadError && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">Daten konnten nicht geladen werden</p>
          </div>
          <Button onClick={() => { setLoading(true); fetchData(); }} variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Zahlungen</h1>
          <p className="text-sm text-slate-600 mt-1">Mietzahlungen, Rechnungen und Berichte</p>
        </div>
        <div className="flex gap-2">
          {/* CSV Import Button */}
          <Dialog open={csvDialogOpen} onOpenChange={(open) => { setCsvDialogOpen(open); if (!open) setCsvImportData(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex-1 sm:flex-none" data-testid="csv-import-btn">
                <Upload className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">CSV Import</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl w-[98vw] mx-2 max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Bankkontoauszug importieren</DialogTitle>
              </DialogHeader>
              
              {!csvImportData ? (
                <div className="space-y-3">
                  {/* Month Selection */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Label className="text-sm font-medium text-slate-700 whitespace-nowrap">Import für Monat:</Label>
                    <Input
                      type="month"
                      value={csvImportMonth}
                      onChange={(e) => setCsvImportMonth(e.target.value)}
                      className="w-48"
                    />
                  </div>
                  
                  <div className="p-6 border-2 border-dashed border-slate-200 rounded-lg text-center">
                    <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                    <p className="text-sm text-slate-600 mb-3">
                      CSV-Datei von Ihrer Sparkasse hochladen
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="hidden"
                      id="csv-upload-input"
                      disabled={csvUploading}
                    />
                    <label htmlFor="csv-upload-input">
                      <Button asChild disabled={csvUploading}>
                        <span className="cursor-pointer">
                          {csvUploading ? (
                            <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Wird hochgeladen...</>
                          ) : (
                            <><Upload className="h-4 w-4 mr-2" /> CSV auswählen</>
                          )}
                        </span>
                      </Button>
                    </label>
                  </div>
                  
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 text-sm mb-1">Unterstützte Formate</h4>
                    <ul className="text-xs text-blue-700 space-y-0.5">
                      <li>• Sparkasse CSV-Export (Standard-Format)</li>
                      <li>• Spalten: Buchungstag, Auftraggeber/Empfänger, Verwendungszweck, Betrag</li>
                      <li>• Nur positive Beträge (Eingänge) werden importiert</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Month indicator */}
                  <div className="p-2 bg-slate-100 rounded-lg text-center">
                    <span className="text-sm font-medium text-slate-700">Import für: {csvImportMonth}</span>
                  </div>
                  
                  {/* Import Summary */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-2 bg-slate-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-slate-900">{csvImportData.total_transactions}</p>
                      <p className="text-xs text-slate-600">Transaktionen</p>
                    </div>
                    <div className="p-2 bg-emerald-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-emerald-600">{csvImportData.matched_transactions}</p>
                      <p className="text-xs text-emerald-700">Zugeordnet</p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-lg text-center">
                      <p className="text-xl font-bold text-amber-600">{csvImportData.transactions?.filter(t => t.status === 'unmatched').length || 0}</p>
                      <p className="text-xs text-amber-700">Offen</p>
                    </div>
                  </div>
                  
                  {/* Transactions List */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[450px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left font-medium text-slate-700 whitespace-nowrap">Datum</th>
                          <th className="px-2 py-2 text-left font-medium text-slate-700">Sender</th>
                          <th className="px-2 py-2 text-left font-medium text-slate-700" style={{width: '40%'}}>Referenz / Verwendungszweck</th>
                          <th className="px-2 py-2 text-right font-medium text-slate-700 whitespace-nowrap">Betrag</th>
                          <th className="px-2 py-2 text-center font-medium text-slate-700">Status</th>
                          <th className="px-2 py-2 text-right font-medium text-slate-700">Aktion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {csvImportData.transactions?.map((tx) => (
                          <tr key={tx.id} className={`hover:bg-slate-50 ${tx.status === 'ignored' ? 'opacity-50' : ''}`}>
                            <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{tx.date}</td>
                            <td className="px-2 py-1.5 font-medium truncate max-w-[180px]" title={tx.sender_name}>{tx.sender_name}</td>
                            <td className="px-2 py-1.5 text-slate-500" title={tx.reference}>
                              <div className="truncate">{tx.reference}</div>
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono font-medium whitespace-nowrap">€{tx.amount.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-center">
                              {tx.status === 'matched' ? (
                                <Badge className={`text-[10px] px-1.5 py-0.5 ${tx.match_type === 'new_payment' ? 'bg-blue-100 text-blue-800' : 'status-paid'}`}>
                                  {tx.matched_guest_name}
                                  {tx.match_type === 'new_payment' && <span className="ml-1">(neu)</span>}
                                </Badge>
                              ) : tx.status === 'ignored' ? (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5">Ignoriert</Badge>
                              ) : (
                                <Badge className="status-pending text-[10px] px-1.5 py-0.5">Offen</Badge>
                              )}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              {tx.status === 'unmatched' && (
                                <div className="flex justify-end gap-1">
                                  <Select onValueChange={(paymentId) => handleManualMatch(tx.id, paymentId)}>
                                    <SelectTrigger className="h-6 w-[110px] text-[10px]">
                                      <SelectValue placeholder="Zuordnen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {payments
                                        .filter(p => p.status !== 'paid')
                                        .sort((a, b) => (a.guest_name || '').localeCompare(b.guest_name || '', 'de'))
                                        .map(p => (
                                        <SelectItem key={p.id} value={p.id} className="text-xs">
                                          {p.guest_name} - €{p.amount}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleIgnoreTransaction(tx.id)} title="Ignorieren">
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3 pt-2 border-t border-slate-200">
                    <Button variant="outline" className="flex-1" onClick={() => setCsvImportData(null)}>
                      Neue Datei
                    </Button>
                    <Button 
                      className="flex-1" 
                      style={{ backgroundColor: BRAND_COLOR }} 
                      onClick={handleProcessImport}
                      disabled={csvProcessing || csvImportData.transactions?.filter(t => t.status === 'matched').length === 0}
                    >
                      {csvProcessing ? (
                        <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Wird verarbeitet...</>
                      ) : (
                        <><Check className="h-4 w-4 mr-2" /> {csvImportData.transactions?.filter(t => t.status === 'matched').length || 0} Zahlungen verbuchen</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Report Button */}
          <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex-1 sm:flex-none" data-testid="report-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Bericht</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4 sm:mx-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Zahlungsbericht erstellen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Preset Selection */}
                <div className="space-y-2">
                  <Label>Zeitraum wählen</Label>
                  <Select value={reportFilter.preset} onValueChange={handlePresetChange}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="this_month">Dieser Monat</SelectItem>
                      <SelectItem value="last_month">Letzter Monat</SelectItem>
                      <SelectItem value="last_3_months">Letzte 3 Monate</SelectItem>
                      <SelectItem value="last_6_months">Letzte 6 Monate</SelectItem>
                      <SelectItem value="this_year">Dieses Jahr</SelectItem>
                      <SelectItem value="last_year">Letztes Jahr</SelectItem>
                      <SelectItem value="custom">Benutzerdefiniert</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Custom Date Range */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Von</Label>
                    <Input
                      type="date"
                      value={reportFilter.start_date}
                      onChange={(e) => setReportFilter({ ...reportFilter, preset: "custom", start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bis</Label>
                    <Input
                      type="date"
                      value={reportFilter.end_date}
                      onChange={(e) => setReportFilter({ ...reportFilter, preset: "custom", end_date: e.target.value })}
                    />
                  </div>
                </div>
                
                {/* Report Type */}
                <div className="space-y-2">
                  <Label>Status-Filter</Label>
                  <Select value={reportFilter.report_type} onValueChange={(v) => setReportFilter({ ...reportFilter, report_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Zahlungen</SelectItem>
                      <SelectItem value="paid">Nur bezahlt</SelectItem>
                      <SelectItem value="pending">Nur offen</SelectItem>
                      <SelectItem value="overdue">Nur überfällig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Download Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={previewReportPdf} 
                    variant="outline" 
                    className="flex-1"
                    disabled={previewLoading}
                  >
                    {previewLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    Vorschau
                  </Button>
                  <Button onClick={downloadReportPdf} className="flex-1" style={{ backgroundColor: BRAND_COLOR }}>
                    <FileText className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                  <Button onClick={downloadReportExcel} variant="outline" className="flex-1">
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* New Payment Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex-1 sm:flex-none" style={{ backgroundColor: BRAND_COLOR }} data-testid="add-payment-btn">
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Neue Zahlung</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md mx-4 sm:mx-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Neue Zahlung</DialogTitle></DialogHeader>
              <form onSubmit={handleCreatePayment} className="space-y-4">
                <div className="space-y-2">
                  <Label>Aufnahme *</Label>
                  <Select value={formData.booking_id} onValueChange={(v) => { setFormData((p) => ({ ...p, booking_id: v })); const b = bookings.find((x) => x.id === v); if (b) setFormData((p) => ({ ...p, amount: b.monthly_rent.toString() })); }}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent>{[...bookings].sort((a, b) => (a.guest_name || '').localeCompare(b.guest_name || '', 'de')).map((b) => <SelectItem key={b.id} value={b.id}>{b.guest_name} - Zi. {b.room_number}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Betrag (€) *</Label><Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData((p) => ({ ...p, amount: e.target.value }))} required /></div>
                  <div className="space-y-2"><Label>Fällig *</Label><Input type="date" value={formData.due_date} onChange={(e) => setFormData((p) => ({ ...p, due_date: e.target.value }))} required /></div>
                </div>
                <div className="space-y-2"><Label>Beschreibung</Label><Input value={formData.description} onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} placeholder="z.B. Miete Januar" /></div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Abbrechen</Button>
                  <Button type="submit" className="flex-1" style={{ backgroundColor: BRAND_COLOR }} disabled={!formData.booking_id || !formData.amount || !formData.due_date}>Erstellen</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div><p className="text-xs text-slate-600">Offen</p><p className="text-lg sm:text-xl font-bold text-amber-600">{stats.pending}</p></div>
            <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-200" />
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div><p className="text-xs text-slate-600">Überfällig</p><p className="text-lg sm:text-xl font-bold" style={{ color: BRAND_COLOR }}>{stats.overdue}</p></div>
            <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: "#f5c6c7" }} />
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div><p className="text-xs text-slate-600">Bezahlt</p><p className="text-lg sm:text-xl font-bold text-emerald-600">{stats.paid}</p></div>
            <Check className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-200" />
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4 flex items-center justify-between">
            <div><p className="text-xs text-slate-600">Offen €</p><p className="text-lg sm:text-xl font-bold font-mono text-slate-900">€{stats.totalPending.toFixed(0)}</p></div>
            <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-slate-200" />
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="all" className="text-xs sm:text-sm">Alle</TabsTrigger>
            <TabsTrigger value="pending" className="text-xs sm:text-sm">Offen</TabsTrigger>
            <TabsTrigger value="overdue" className="text-xs sm:text-sm">Überfällig</TabsTrigger>
            <TabsTrigger value="paid" className="text-xs sm:text-sm">Bezahlt</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Payments List */}
      <Card className="border-slate-200">
        <CardHeader className="p-4"><CardTitle className="text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>Zahlungen ({filteredPayments.length})</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0">
          {filteredPayments.length > 0 ? (
            <div className="space-y-2">
              {filteredPayments.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`payment-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{p.guest_name}</div>
                    <div className="text-xs text-slate-500">Zi. {p.room_number} • {format(new Date(p.due_date), "dd.MM.yy", { locale: de })}</div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <div className="text-right hidden sm:block">
                      <div className="text-sm font-mono">€{p.amount.toFixed(0)}</div>
                      {p.paid_amount > 0 && <div className="text-xs text-emerald-600">-€{p.paid_amount.toFixed(0)}</div>}
                    </div>
                    {getStatusBadge(p.status)}
                    
                    {/* Actions Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" disabled={generatingPdf === p.id}>
                          {generatingPdf === p.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                          ) : (
                            <MoreVertical className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {p.status !== "paid" && (
                          <>
                            <DropdownMenuItem onClick={() => openRecordDialog(p)}>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Zahlung verbuchen
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem onClick={() => previewInvoice(p)}>
                          <Eye className="h-4 w-4 mr-2" />
                          Rechnung (Vorschau)
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => previewMahnung(p, 1)}>
                          <Eye className="h-4 w-4 mr-2" />
                          1. Erinnerung
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => previewMahnung(p, 2)}>
                          <Eye className="h-4 w-4 mr-2" />
                          2. Mahnung
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => previewMahnung(p, 3)} className="text-red-600">
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Letzte Mahnung
                        </DropdownMenuItem>
                        {/* Admin only: Delete payment */}
                        {user?.role === "admin" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDeletePayment(p.id, p.guest_name)}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Zahlung löschen
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500"><p className="text-sm">Keine Zahlungen</p></div>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={recordDialogOpen} onOpenChange={setRecordDialogOpen}>
        <DialogContent className="max-w-sm mx-4 sm:mx-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Zahlung erfassen</DialogTitle></DialogHeader>
          {selectedPayment && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Bewohner:</span><span className="font-medium">{selectedPayment.guest_name}</span></div>
                <div className="flex justify-between mt-1"><span className="text-slate-600">Offen:</span><span className="font-mono font-medium">€{(selectedPayment.amount - selectedPayment.paid_amount).toFixed(2)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Betrag (€) *</Label><Input type="number" step="0.01" value={recordData.amount} onChange={(e) => setRecordData((p) => ({ ...p, amount: e.target.value }))} required /></div>
                <div className="space-y-2">
                  <Label>Methode</Label>
                  <Select value={recordData.payment_method} onValueChange={(v) => setRecordData((p) => ({ ...p, payment_method: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Überweisung</SelectItem>
                      <SelectItem value="cash">Bar</SelectItem>
                      <SelectItem value="card">Karte</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Referenz</Label><Input value={recordData.reference} onChange={(e) => setRecordData((p) => ({ ...p, reference: e.target.value }))} placeholder="Transaktionsnr." /></div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setRecordDialogOpen(false)} className="flex-1">Abbrechen</Button>
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={!recordData.amount}>Verbuchen</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" style={{ color: BRAND_COLOR }} />
              {previewTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[65vh]">
            {previewLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : previewPdf ? (
              <iframe 
                src={`data:application/pdf;base64,${previewPdf}`}
                className="w-full h-full border rounded-lg"
                title={previewTitle}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400">
                Keine Vorschau verfügbar
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Schließen
            </Button>
            <Button 
              onClick={downloadCurrentPreview}
              disabled={!previewPdf}
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <Download className="h-4 w-4 mr-2" />
              Herunterladen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
